import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { type CreativeTypes } from '@bynder-studio/render-core';
import { getUser } from 'packages/store/user/user.selectors';
import { catchResponseError } from 'packages/helpers/helpers';
import renditionsService, { RenditionConfiguration } from 'packages/services/renditionsService';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from '~/configs/features';

const useRenditionProfiles = ({
    creativeType,
    preferredConfigurationIds,
}: {
    creativeType: CreativeTypes;
    preferredConfigurationIds?: number[];
}) => {
    const dispatch = useDispatch();
    const { isPlatformAllowed } = useAccessRights();
    const { currentCompany } = useSelector(getUser);

    const [isLoadingRenditions, setIsLoadingRenditions] = useState(true);
    const [renditionProfiles, setRenditionProfiles] = useState<RenditionConfiguration[]>([]);
    const [selectedProfile, setSelectedProfile] = useState<RenditionConfiguration | null>(renditionProfiles[0] || null);

    const deselectProfile = () => {
        setSelectedProfile(renditionProfiles[0] || null);
    };

    useEffect(() => {
        if (!currentCompany || !['IMAGE', 'VIDEO'].includes(creativeType)) {
            return;
        }

        renditionsService
            .getRenditionConfigurations(currentCompany, creativeType as 'IMAGE' | 'VIDEO')
            .then(({ json, status }) => {
                if (status !== 200) {
                    return;
                }

                let renditions = json || [];

                if (!isPlatformAllowed([features.PDF])) {
                    renditions = renditions.filter((rendition) => rendition.type !== 'PDF');
                }

                renditions = [...renditions].sort(
                    (a, b) => (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity),
                );

                setRenditionProfiles(renditions);

                const preferred = preferredConfigurationIds?.length
                    ? renditions.find((r) => preferredConfigurationIds.includes(r.id))
                    : undefined;
                setSelectedProfile(preferred ?? renditions[0] ?? null);

                setIsLoadingRenditions(false);
            })
            .catch(catchResponseError);
    }, [dispatch, currentCompany, creativeType, isPlatformAllowed]);

    return {
        isLoadingRenditions,
        setIsLoadingRenditions,
        renditionProfiles,
        setRenditionProfiles,
        selectedProfile,
        setSelectedProfile,
        deselectProfile,
    };
};

export default useRenditionProfiles;
